import { useState, useEffect, useRef } from "react";
import { Area } from "../models/Area";
import { fetchUserDraft, fetchAreas } from "../services/ratingsService";
import { mapDraftAnswersToRatings } from "../utils/draftMapper";

interface UseAreasDataProps {
  isLoggedIn: boolean;
  getAccessToken: () => Promise<string>;
}

export const useAreasData = ({ isLoggedIn, getAccessToken }: UseAreasDataProps) => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [draftEntry, setDraftEntry] = useState<any | null>(null);
  const [noActivePeriod, setNoActivePeriod] = useState(false);
  const [turnedIn, setTurnedIn] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setAreas([]);
      setRatings({});
      setDraftEntry(null);
      setNoActivePeriod(false);
      setTurnedIn(false);
      loadedRef.current = false;
      return;
    }
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        // Fetch areas and draft in parallel to avoid sequential state updates
        const [areasData, draftRes] = await Promise.all([
          fetchAreas(getAccessToken),
          fetchUserDraft(getAccessToken),
        ]);

        // Batch all state updates together (single render)
        setAreas(areasData);

        if (draftRes.ok && draftRes.turnedIn) {
          setTurnedIn(true);
          setNoActivePeriod(true);
          setRatings({});
          setDraftEntry(null);
        } else if (draftRes.ok && draftRes.noActivePeriod) {
          setNoActivePeriod(true);
          setTurnedIn(false);
          setDraftEntry(null);
        } else if (draftRes.ok && Array.isArray(draftRes.data)) {
          setNoActivePeriod(false);
          setTurnedIn(false);
          const first = draftRes.data[0];
          setDraftEntry(first);
          const mapped = mapDraftAnswersToRatings(draftRes.data);
          setRatings(mapped.ratings);
        } else {
        }
      } catch (e) {
        loadedRef.current = false;
      }
    })();
  }, [isLoggedIn]);

  // Extract draftId from draftEntry if available
  const draftId = draftEntry?.requestPeriodId || draftEntry?.draftId || draftEntry?.id || null;

  return {
    areas,
    ratings,
    setRatings,
    draftEntry,
    draftId,
    noActivePeriod,
    turnedIn,
    setTurnedIn,
    setNoActivePeriod,
  };
};
